import { DataTable, type DataTableColumn } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { type ActionSummaryRow, adminActionSummaryOptions } from "@/api";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatNumber } from "@/i18n/format";

const numeric = (value: number) => (
  <Text as="span" variant="body-sm" tone="secondary" className="tabular-nums">
    {formatNumber(value)}
  </Text>
);

/**
 * Write actions (MHS-973): how actions that change things outside AnthmAI went, per tool, in chat and in workflows.
 * Counts only: who asked and what was sent are never kept once an action is done.
 */
export function ActionsSummarySection({ startDate }: Readonly<{ startDate: string }>) {
  const { t } = useTranslation("adminUsage", { keyPrefix: "actions" });
  const summary = useQuery({
    ...adminActionSummaryOptions({ query: { start_date: startDate } }),
    select: (r) => r.data,
  });
  const columns: DataTableColumn<ActionSummaryRow>[] = [
    {
      key: "action",
      header: t("columns.action"),
      mobile: "primary",
      render: (row) => (
        <div className="flex flex-col">
          <Text as="span" variant="body-sm">
            {row.integration ? t("toolOf", { tool: row.tool, integration: row.integration }) : row.tool}
          </Text>
          <Text as="span" variant="caption" tone="tertiary">
            {t(`surface.${row.surface}`)}
          </Text>
        </div>
      ),
    },
    { key: "proposed", header: t("columns.proposed"), align: "right", render: (row) => numeric(row.proposed ?? 0) },
    { key: "executed", header: t("columns.executed"), align: "right", render: (row) => numeric(row.executed ?? 0) },
    { key: "declined", header: t("columns.declined"), align: "right", render: (row) => numeric(row.declined ?? 0) },
    {
      key: "lapsed",
      header: t("columns.lapsed"),
      align: "right",
      render: (row) => numeric((row.expired ?? 0) + (row.superseded ?? 0)),
    },
    { key: "failed", header: t("columns.failed"), align: "right", render: (row) => numeric(row.failed ?? 0) },
    { key: "limited", header: t("columns.limited"), align: "right", render: (row) => numeric(row.limited ?? 0) },
  ];
  return (
    <PageSection title={t("title")} description={t("description")}>
      {summary.isError ? (
        <Text tone="error" role="alert">
          {t("loadFailed")}
        </Text>
      ) : (
        <DataTable
          aria-label={t("title")}
          columns={columns}
          rows={summary.data ?? []}
          rowKey={(row) => `${row.integration ?? ""}:${row.tool}:${row.surface}`}
          empty={
            <Text variant="body-sm" tone="tertiary">
              {summary.isPending ? t("loading") : t("empty")}
            </Text>
          }
        />
      )}
    </PageSection>
  );
}
